/**
 * Warranty Tracker Utility Functions
 * Handles date calculations, status determination, and validation for warranty tracking
 */

/**
 * Calculate days remaining until warranty expiration
 * @param {string} warrantyEndDate - ISO date string (YYYY-MM-DD)
 * @returns {number} - Days remaining (negative if expired)
 */
export const calculateDaysRemaining = (warrantyEndDate) => {
  if (!warrantyEndDate) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(warrantyEndDate);
  endDate.setHours(0, 0, 0, 0);

  const diffTime = endDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Add months to a date
 * @param {Date} date - Start date
 * @param {number} months - Number of months to add
 * @returns {Date} - New date
 */
export const addMonths = (date, months) => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
};

/**
 * Format date to ISO string (YYYY-MM-DD)
 * @param {Date|string} date - Date object or date string
 * @returns {string} - ISO formatted date string
 */
export const formatDateISO = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Format as YYYY-MM-DD
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Get warranty status label and color based on days remaining
 * @param {number} daysRemaining - Number of days until expiration (negative if expired)
 * @returns {{ label: string, color: string, chipColor: string }} - Status info
 */
export const getWarrantyStatus = (daysRemaining) => {
  if (daysRemaining < 0) {
    return {
      label: 'Expired',
      color: '#f44336',        // Red
      chipColor: 'error'
    };
  } else if (daysRemaining <= 30) {
    return {
      label: 'Expiring Soon',
      color: '#ff9800',        // Orange
      chipColor: 'warning'
    };
  } else if (daysRemaining <= 90) {
    return {
      label: 'Active',
      color: '#ffc107',        // Yellow/Amber
      chipColor: 'warning'
    };
  } else {
    return {
      label: 'Active',
      color: '#4caf50',        // Green
      chipColor: 'success'
    };
  }
};

/**
 * Validate serial number uniqueness within property
 * @param {string} serialNumber - Serial number to validate
 * @param {Array} existingWarranties - Array of existing warranty objects
 * @param {string} currentWarrantyId - For edit mode, exclude this ID from check
 * @returns {boolean} - True if unique, false if duplicate
 */
export const isSerialNumberUnique = (serialNumber, existingWarranties, currentWarrantyId = null) => {
  if (!serialNumber || !serialNumber.trim()) return false;

  return !existingWarranties.some(w =>
    w.serialNumber.toLowerCase().trim() === serialNumber.toLowerCase().trim() &&
    w.id !== currentWarrantyId
  );
};

/**
 * Calculate warranty end date from start date and duration
 * @param {string|Date} startDate - Warranty start date
 * @param {number} durationMonths - Duration in months
 * @returns {string} - ISO formatted end date (YYYY-MM-DD)
 */
export const calculateWarrantyEndDate = (startDate, durationMonths) => {
  if (!startDate || !durationMonths) return '';

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endDate = addMonths(start, durationMonths);

  return formatDateISO(endDate);
};

/**
 * Convert duration from days/months/years to months
 * @param {number} value - Duration value
 * @param {string} unit - Unit: 'days', 'months', or 'years'
 * @returns {number} - Duration in months
 */
export const convertToMonths = (value, unit) => {
  if (!value || value <= 0) return 0;

  switch (unit) {
    case 'days':
      return Math.ceil(value / 30); // Approximate: 30 days = 1 month
    case 'months':
      return value;
    case 'years':
      return value * 12;
    default:
      return 0;
  }
};

/**
 * Convert months to display format (days, months, or years)
 * @param {number} months - Duration in months
 * @returns {{ value: number, unit: string, display: string }} - Formatted duration
 */
export const formatDuration = (months) => {
  if (!months || months === 0) {
    return { value: 0, unit: 'months', display: '0 months' };
  }

  // If less than 1 month, show as days
  if (months < 1) {
    const days = Math.round(months * 30);
    return { value: days, unit: 'days', display: `${days} ${days === 1 ? 'day' : 'days'}` };
  }

  // If divisible by 12, show as years
  if (months % 12 === 0) {
    const years = months / 12;
    return { value: years, unit: 'years', display: `${years} ${years === 1 ? 'year' : 'years'}` };
  }

  // Otherwise show as months
  return { value: months, unit: 'months', display: `${months} ${months === 1 ? 'month' : 'months'}` };
};

/**
 * Get local date string in YYYY-MM-DD format (used for date pickers)
 * @returns {string} - Today's date in ISO format
 */
export const getLocalDateString = () => {
  const today = new Date();
  return formatDateISO(today);
};

/**
 * Parse ISO date string to Date object for date pickers
 * @param {string} isoDate - ISO date string (YYYY-MM-DD)
 * @returns {Date} - Date object
 */
export const parseISODate = (isoDate) => {
  if (!isoDate) return new Date();
  return new Date(isoDate + 'T00:00:00'); // Add time to avoid timezone issues
};

/**
 * Validate warranty form data
 * @param {Object} formData - Form data to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateWarrantyForm = (formData) => {
  const {
    selectedItem,
    serialNumber,
    purchaseDate,
    warrantyMode,
    selectedWarrantyType,
    customDuration,
    customDurationUnit
  } = formData;

  if (!selectedItem || !selectedItem.id) {
    return "Please select an item";
  }

  if (!serialNumber || !serialNumber.trim()) {
    return "Serial number is required";
  }

  if (!purchaseDate) {
    return "Purchase date is required";
  }

  if (warrantyMode === "template" && (!selectedWarrantyType || !selectedWarrantyType.id)) {
    return "Please select a warranty type";
  }

  if (warrantyMode === "custom") {
    if (!customDuration || customDuration <= 0) {
      return "Please enter a valid warranty duration";
    }
    if (!customDurationUnit) {
      return "Please select a duration unit (days/months/years)";
    }
  }

  return null; // Valid
};
